import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button, Flex } from "antd"
import { routes } from "router"
import { BOOKING_MODES } from "pagesV2/pro/Bookings/constants"
import { getApiErrorMessage } from "helpers/embedded"
import {
  addToWidgetCart,
  changeWidgetCartItemChoice,
  fetchExperienceBookingDetails,
  hasCartError,
  resetBookingSelections,
  updateBookingSelections
} from "store/experiences"
import { getMultiDaySpan, getMultiDayStartTimes, normalizeTimeSlots } from "utils/experienceAvailability"
import { buildPanelSummary } from "../pricing"
import { describeAvailabilityRefusal } from "../../CartHold/holdIssue"
import ReservationExpiredNotice from "../../CartHold/ReservationExpiredNotice"
import useCartHold from "../../CartHold/useCartHold"
import useCartItemBeingChanged from "./useCartItemBeingChanged"
import { CART_ERRORS } from "../../constants"
import { buildInitialCounts, getTotalGuests } from "../constants"
import DateSection from "./DateSection"
import DurationSection from "./DurationSection"
import StartTimeSection from "./StartTimeSection"
import GuestsSection from "./GuestsSection"
import PriceSummary from "./PriceSummary"
import PanelFrame from "./PanelFrame"
import styles from "./BookingPanel.module.css"

const NO_ERRORS = { date: null, duration: null, time: null, guests: null }

// "Real-Time Availability" panel for a private experience: date -> duration -> start time -> guests,
// with the live price. A multi-day trip has no duration step: its start date covers the whole span
// and its start times come from its availability schedules. The choices are a draft in the shared
// bookingSelections store; Add to Cart and Book Now add a copy to the cart (Book Now then skips
// the cart page), so browsing other experiences never changes what is in the cart. Opened from a
// cart item's "Choose another time" it starts from that item's choices and Save Changes changes it.
const BookingPanel = ({ experience, isSheetOpen, onSheetClose }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { uuid, id } = useParams()
  const { bookingData, bookingSelections } = useSelector((state) => state.experiences.experienceBookingData)
  const changing = useCartItemBeingChanged()
  const [slots, setSlots] = useState([])
  const [isSlotsLoading, setIsSlotsLoading] = useState(false)
  const [errors, setErrors] = useState(NO_ERRORS)
  const [isAdding, setIsAdding] = useState(false)
  // Why Add to Cart / Book Now was refused, shown by the buttons until the choice changes.
  const [addError, setAddError] = useState(null)
  // Only the latest slots request may update the chips (a quick date change can overtake a slow one).
  const slotsRequestRef = useRef(0)

  const durations = bookingData?.trip_durations || []
  const minGuests = bookingData?.min_guests ?? experience?.min_guests ?? 0
  const maxGuests = bookingData?.max_guests ?? experience?.max_guests ?? 0
  const { date, tripLength, timeSlot, counts } = bookingSelections
  const priceData = tripLength?.price || null
  const isMultiDay = experience?.duration_type === "multi_day"
  const spanDays = isMultiDay ? getMultiDaySpan(experience) : 1

  const loadSlots = (nextDate, duration) => {
    const requestId = ++slotsRequestRef.current
    setSlots([])
    if (isMultiDay) {
      setIsSlotsLoading(false)
      setSlots(getMultiDayStartTimes(experience, nextDate))
      return
    }
    if (!nextDate || !duration?.key) {
      setIsSlotsLoading(false)
      return
    }
    setIsSlotsLoading(true)
    dispatch(fetchExperienceBookingDetails(id, "available_slots", duration.key, nextDate, undefined, BOOKING_MODES.embedded))
      .then((response) => {
        if (requestId === slotsRequestRef.current) setSlots(normalizeTimeSlots(response?.data))
      })
      .catch(() => {})
      .finally(() => {
        if (requestId === slotsRequestRef.current) setIsSlotsLoading(false)
      })
  }

  const selectDuration = (duration, nextDate = date) => {
    const hasGuests = getTotalGuests(counts) > 0
    dispatch(
      updateBookingSelections({
        tripLength: duration,
        timeSlot: null,
        counts: hasGuests ? counts : buildInitialCounts(duration?.price, minGuests)
      })
    )
    setErrors((prev) => ({ ...prev, duration: null, time: null }))
    loadSlots(nextDate, duration)
  }

  // Coming back to the same experience keeps the draft; another experience starts a new one (the
  // cart can hold this experience more than once, so it is not a draft source). Changing a cart item
  // starts from its choices. Slots for a kept date + duration are reloaded.
  useEffect(() => {
    if (!id) return
    if (changing.selection) {
      dispatch(resetBookingSelections())
      dispatch(updateBookingSelections({ ...changing.selection, experienceId: id }))
    }
    const kept = changing.selection || (bookingSelections.experienceId === id ? bookingSelections : null)
    if (!kept) {
      dispatch(resetBookingSelections())
      dispatch(updateBookingSelections({ experienceId: id }))
    }
    dispatch(fetchExperienceBookingDetails(id, "trip_data", "", "", undefined, BOOKING_MODES.embedded)).catch(() => {})
    if (kept) loadSlots(kept.date, kept.tripLength)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, changing.cartId, dispatch])

  // A single duration needs no choice.
  useEffect(() => {
    if (isMultiDay) return
    if (durations.length === 1 && tripLength?.key !== durations[0].key) selectDuration(durations[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durations, isMultiDay])

  // Multi-day trips price from their first trip price, as the old widget does.
  useEffect(() => {
    const tripPrice = experience?.trip_prices?.[0]
    if (!isMultiDay || !tripPrice || tripLength?.key === tripPrice.id) return
    selectDuration({ key: tripPrice.id, value: "", price: tripPrice })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiDay, experience?.trip_prices])

  // A single start time needs no choice either.
  useEffect(() => {
    if (slots.length === 1 && !timeSlot) dispatch(updateBookingSelections({ timeSlot: slots[0] }))
  }, [slots, timeSlot, dispatch])

  const handleDateChange = (nextDate) => {
    dispatch(updateBookingSelections({ date: nextDate, timeSlot: null }))
    setErrors((prev) => ({ ...prev, date: null, time: null }))
    loadSlots(nextDate, tripLength)
  }

  const handleTimeChange = (slot) => {
    dispatch(updateBookingSelections({ timeSlot: slot }))
    setErrors((prev) => ({ ...prev, time: null }))
  }

  const handleGuestsChange = (nextCounts) => {
    dispatch(updateBookingSelections({ counts: nextCounts }))
    setErrors((prev) => ({ ...prev, guests: null }))
  }

  const summary = useMemo(
    () => (priceData && getTotalGuests(counts) > 0 ? buildPanelSummary({ experience, priceData, date, counts }) : null),
    [experience, priceData, date, counts]
  )

  const validate = () => {
    const requiredGuests = Math.max(1, minGuests || 0)
    const nextErrors = {
      date: date ? null : t("booking_widget.experience.errors.date"),
      duration: tripLength ? null : t("booking_widget.experience.errors.duration"),
      // A multi-day trip without a scheduled start time on that day can still be booked.
      time: timeSlot || (isMultiDay && date && slots.length === 0) ? null : t("booking_widget.experience.errors.time"),
      guests: getTotalGuests(counts) >= requiredGuests ? null : t("booking_widget.experience.errors.guests", { count: requiredGuests })
    }
    setErrors(nextErrors)
    return Object.values(nextErrors).every((error) => !error)
  }

  // A new choice starts without the last refusal.
  useEffect(() => {
    setAddError(null)
  }, [date, tripLength, timeSlot, counts])

  // While the cart's reservation is over nothing more is added until "Check availability & continue"
  // reserves it again (the API refuses, hold_expired).
  const { isReservationOver } = useCartHold()

  // Added to the cart with the priced amount, as the old widget does, once its places are held
  // (addToWidgetCart; the same date and start time changes the item already there). A start time
  // that's gone is refused, and the start times are loaded again.
  const handleContinue = async (path) => {
    if (isAdding || isReservationOver || !validate()) return
    setIsAdding(true)
    try {
      const selection = { ...bookingSelections, isMultiDay, ...(summary && { amount: summary.total }) }
      await dispatch(changing.cartId ? changeWidgetCartItemChoice(changing.cartId, selection) : addToWidgetCart(selection))
      navigate(path)
    } catch (error) {
      setIsAdding(false)
      // The cart was read again and now shows the expired notice
      if (hasCartError(error, CART_ERRORS.holdExpired)) return
      const warning = describeAvailabilityRefusal({ message: error?.data?.message, experience, t })
      setAddError(
        warning
          ? `${t("booking_widget.experience.availability_changed")}: ${warning}`
          : getApiErrorMessage(error) || t("booking_widget.checkout.errors.generic")
      )
      if (warning) loadSlots(date, tripLength)
    }
  }

  const actions = (
    <>
      {addError && (
        <span className={styles.errorText} role="alert">
          {addError}
        </span>
      )}
      {isReservationOver && <ReservationExpiredNotice />}
      {changing.cartId && <span className={styles.paymentNote}>{t("booking_widget.experience.changing_item")}</span>}
      <Button
        type="primary"
        shape="round"
        size="large"
        block
        loading={isAdding}
        disabled={isReservationOver}
        onClick={() => handleContinue(routes.widgetCart({ uuid }))}
      >
        {t(changing.cartId ? "booking_widget.experience.save_changes" : "booking_widget.experience.add_to_cart")}
      </Button>
      {!changing.cartId && (
        <Button
          shape="round"
          size="large"
          block
          color="primary"
          variant="outlined"
          disabled={isAdding || isReservationOver}
          onClick={() => handleContinue(routes.widgetCheckout({ uuid }))}
        >
          {t("booking_widget.experience.book_now")}
        </Button>
      )}
      <span className={styles.paymentNote}>{t("booking_widget.experience.payment_note")}</span>
    </>
  )

  return (
    <PanelFrame isSheetOpen={isSheetOpen} onSheetClose={onSheetClose} actions={actions}>
      {isMultiDay && (
        <Flex vertical gap={2} className={styles.multiDayBanner}>
          <span className={styles.multiDayBannerTitle}>{t("booking_widget.experience.multi_day_title", { count: spanDays })}</span>
          <span>{t("booking_widget.experience.multi_day_hint")}</span>
        </Flex>
      )}
      {/* Keyed by the draft's experience: the calendar reopens on the right month once this
          experience's draft replaces the previous one. */}
      <DateSection key={bookingSelections.experienceId} experience={experience} value={date} onChange={handleDateChange} error={errors.date} spanDays={spanDays} />
      {!isMultiDay && durations.length > 1 && (
        <DurationSection
          experience={experience}
          durations={durations}
          selectedKey={tripLength?.key}
          date={date}
          onSelect={(duration) => selectDuration(duration)}
          error={errors.duration}
        />
      )}
      <StartTimeSection
        slots={slots}
        selectedSlot={timeSlot}
        isLoading={isSlotsLoading}
        hasDate={!!date && !!tripLength}
        onSelect={handleTimeChange}
        error={errors.time}
      />
      {priceData && (
        <GuestsSection
          experience={experience}
          priceData={priceData}
          date={date}
          counts={counts}
          minGuests={minGuests}
          maxGuests={maxGuests}
          onChange={handleGuestsChange}
          error={errors.guests}
        />
      )}
      {summary && <PriceSummary summary={summary} />}
    </PanelFrame>
  )
}

BookingPanel.propTypes = {
  experience: PropTypes.object,
  isSheetOpen: PropTypes.bool,
  onSheetClose: PropTypes.func
}

BookingPanel.defaultProps = {
  experience: null,
  isSheetOpen: false,
  onSheetClose: () => {}
}

export default BookingPanel
